/**
 * admissionsApi — the single typed surface over the POST-AIQ-143 admissions
 * backend (routes/admissions.py). Every admissions component calls THROUGH
 * here rather than hand-rolling axios URLs, so the endpoint shapes,
 * the consent-gate `?student_id=` threading, and the error-classification
 * live in ONE place and can't drift between the four UIs.
 *
 * WHY a module (not inline axios like Prep.js): the admissions surface is
 * larger (10 endpoints) and three of its components must react identically
 * to the SAME backend status codes — a 403 means "no consent → route to the
 * guardian-consent CTA"; a 404 means "feature off (the bare "Not Found") OR
 * no profile yet → empty state", and any other 404 is a failure to retry.
 * Centralizing that classification (see `classifyAdmissionsError`)
 * guarantees Profile / Shortlist / Advisor branch the same way on the same
 * failure, which is the "no dead ends" contract.
 *
 * `studentId` (optional, last arg on the gated reads/writes): a guardian
 * acting on a verified-linked minor passes the minor's UUID, which the gate
 * dependency reads as `?student_id=`. Omitted → the caller acts as
 * themselves (the student's own view). The BACKEND enforces the link +
 * consent; this is purely plumbing the query param uniformly.
 */
import axios from 'axios';
import { API_URL, HELD_REQUEST_TIMEOUT_MS, SEND_ONCE } from '../config';
import { friendlyError, makeWithStudent, readMessage } from '../api/httpError';

const BASE = `${API_URL}/api/v1/admissions`;

// `withStudent` + `friendlyError` + `readMessage` are the shared envelope
// readers (frontend/src/api/httpError.js), hoisted there in the H4 unify (#68)
// so admissions and career thread `?student_id=` and read the error envelope
// IDENTICALLY. `friendlyError` is re-exported so the existing
// `import { friendlyError } from './admissionsApi'` call sites are unchanged
// (and it now also reads the canonical `error.message`, not just the legacy
// top-level `detail` — the pre-unify admissions copy missed the envelope).
const withStudent = makeWithStudent(BASE);
export { friendlyError };

// ── Profile ────────────────────────────────────────────────────────────────

export async function getProfile(studentId) {
  const res = await axios.get(withStudent('/profile', studentId));
  return res.data;
}

export async function saveProfile(body, studentId) {
  const res = await axios.put(withStudent('/profile', studentId), body);
  // The per-school "You vs this school" rows and the fit read are computed from
  // THIS profile, so every cached school detail is now stale (see below).
  clearSchoolDetailCache();
  return res.data;
}

// ── Shortlist + counterfactual + targets ────────────────────────────────────

export async function getShortlist(studentId) {
  const res = await axios.get(withStudent('/shortlist', studentId));
  return res.data;
}

export async function getCounterfactuals(studentId) {
  // POST (a compute action over profile + catalog) — no body in v1; the
  // engine relaxes every currently-limiting dimension and diffs.
  const res = await axios.post(withStudent('/counterfactual', studentId), {});
  return res.data;
}

export async function listTargets(studentId) {
  const res = await axios.get(withStudent('/targets', studentId));
  return res.data;
}

export async function addTarget(schoolId, source, studentId) {
  const body = { school_id: schoolId };
  if (source) body.source = source;
  const res = await axios.post(withStudent('/targets', studentId), body);
  return res.data;
}

/**
 * Remove a school from the shortlist (DELETE /admissions/targets/{school_id}).
 *
 * 204 No Content on success (idempotent — removing a non-listed school is a
 * no-op 204, not a 404).
 *
 * THE CONFIRM GATE (ADM PR1, A4): milestones CASCADE with their target, so a
 * target that HAS saved milestones is refused with a 409 whose envelope carries
 * `error.code = "has_milestones"` and `error.details.milestone_count`. The
 * caller raises a dialog NAMING that count and retries with `{confirm: true}`,
 * which appends `?confirm=true` and lets the delete (and its cascade) proceed.
 * A milestone-FREE target deletes on the first call — the common case is never
 * a two-step.
 */
export async function removeTarget(schoolId, studentId, { confirm = false } = {}) {
  const path = confirm
    ? `/targets/${schoolId}?confirm=true`
    : `/targets/${schoolId}`;
  // `withStudent` picks `&` when the path already carries a query string, so
  // the confirmed form threads `?confirm=true&student_id=…` correctly.
  await axios.delete(withStudent(path, studentId));
}

// ── Catalog search — the "add a school" typeahead (ADM PR1, D6/A1/A7) ────────

/**
 * Find catalog schools by name or alias (GET /admissions/schools/search).
 *
 * `q` must be ≥2 non-space characters — the backend 422s below that, and the
 * combobox enforces the SAME floor client-side (A8) so ordinary typing never
 * produces a request that can only fail. `level` ('secondary' | 'college')
 * narrows the catalog; omitted, the backend falls back to the student's profile
 * track and, with NO profile, searches both levels — which is what keeps the
 * search box working on the no-profile empty state (A7).
 *
 * Returns { query, level, count, results: [{school_id, name, city, state,
 * level, matched_on, already_on_list}], truncated, next_steps }. Rate-limited
 * on a DEDICATED search bucket, so a 429 here means "typing too fast", never
 * "you exhausted your questions" — the caller shows a calm retry, never red.
 */
export async function searchSchools(q, level = null, studentId = null, limit = null) {
  const params = new URLSearchParams({ q });
  if (level) params.set('level', level);
  if (limit) params.set('limit', String(limit));
  const res = await axios.get(
    withStudent(`/schools/search?${params.toString()}`, studentId),
  );
  return res.data;
}

// ── Catalog browse — the "Explore schools" research filter (ADM-REQ PR B, D5) ─

// The query params the browse route accepts, in the order they are serialized.
// A fixed order keeps the URL deterministic, and a key that is not on this list
// can never ride along into the request.
const BROWSE_PARAMS = [
  'level', 'state', 'setting', 'control', 'archetype',
  'exam_gated', 'max_cost_usd', 'q', 'cursor', 'limit',
];

/**
 * Filter the catalog (GET /admissions/schools/browse) → `SchoolBrowseResponse`:
 * { level, count, results: [{school_id, name, city, state, level, control,
 * setting, selectivity_tier, admit_type, test_policy, exam_gated,
 * annual_cost_usd, net_price_usd, academic_focus, archetype_slug,
 * archetype_name, already_on_list}], next_cursor, next_steps }.
 *
 * `params` is an object over BROWSE_PARAMS and ONLY the non-empty values are
 * serialized: null, undefined, '' (or whitespace) and NaN are dropped, so a
 * form can hand over its raw state without pre-filtering. `false` and `0` are
 * real values and ARE sent (`exam_gated=false` is "no exam schools"; a $0 cost
 * ceiling is legal). This is transport, not validation: the backend 422s a
 * 1-letter `state` or `q`, and the caller owns those floors (ExploreSchools
 * mirrors them so ordinary typing never produces a request that could only fail).
 *
 * `next_cursor` is OPAQUE — hand it back verbatim as `cursor`, with the SAME
 * filters that produced it. Rate-limited on the same search bucket as the
 * typeahead and the school detail, so a 429 is a pace signal (a calm retry,
 * never red). `studentId` threads a guardian acting on a linked minor, whose
 * pins decide each row's `already_on_list`.
 */
export async function browseSchools(params = {}, studentId = null) {
  const qs = new URLSearchParams();
  BROWSE_PARAMS.forEach((key) => {
    const value = params?.[key];
    if (value === null || value === undefined) return;
    if (typeof value === 'number' && Number.isNaN(value)) return;
    const text = typeof value === 'string' ? value.trim() : String(value);
    if (text) qs.set(key, text);
  });
  const query = qs.toString();
  const res = await axios.get(
    withStudent(query ? `/schools/browse?${query}` : '/schools/browse', studentId),
  );
  return res.data;
}

// ── Per-school detail — "What {school} looks for" (ADM research PR A, D4) ─────

// The detail payload is one server-side compute per school (requirements +
// "You vs this school" + the fit read), so it is read ONCE per panel open and
// PREFETCHED on hover/focus of the toggle (the preload rule: the click then
// lands on an already-running or finished request). The cache is what makes a
// prefetch free to share with the click that follows it.
const DETAIL_CACHE_MAX = 50;
// A bound on staleness for the things only the server changes (catalog edits, a
// guardian updating the profile elsewhere). A profile save in THIS tab clears
// it outright (`saveProfile`).
const DETAIL_CACHE_TTL_MS = 5 * 60 * 1000;

// `${studentId}|${schoolId}` → { promise, at }. A Map iterates in insertion
// order, so deleting + re-inserting on every hit keeps the OLDEST-USED entry
// first, which is the one evicted.
const _detailCache = new Map();
let _detailCacheScope = null;

// The ONE definition of an entry's key: the read and `invalidateSchoolDetail`
// must agree on it, or an invalidation would silently miss the entry it meant.
const detailKey = (schoolId, studentId) => `${studentId || ''}|${schoolId}`;

/**
 * Who is signed in, as far as the cache is concerned. The payload carries the
 * student's OWN scores and GPA compared against the school, and logout does not
 * reload the page — App.js wipes in-memory PII caches by hand (`handleLogout`).
 * A module-level cache must not depend on every future caller remembering that:
 * the stored token changing is the signal that another account is now at the
 * keyboard (shared device, a sibling signing in), so whatever was cached under
 * the previous token is dropped before it can be served.
 */
function detailCacheScope() {
  try {
    return localStorage.getItem('token') || '';
  } catch {
    return '';
  }
}

/** Drop every cached school detail (a profile write; a logout hook; tests). */
export function clearSchoolDetailCache() {
  _detailCache.clear();
}

/**
 * Drop ONE school's cached detail (ADM research PR E: a calendar date saved to
 * the timeline).
 *
 * The payload's calendar items carry `milestone_id` — "this date is already on
 * your timeline" — so after "Add to my timeline" the cached copy is stale in
 * exactly that field, and a remount (or the next open) must read a fresh one.
 * It is not a profile write, so `clearSchoolDetailCache` would be the wrong
 * tool: every OTHER school's entry is still good, and re-spending its
 * server-side compute (and a rate-limit token) for nothing is the cost. A
 * school that was never cached is a no-op.
 */
export function invalidateSchoolDetail(schoolId, studentId = null) {
  _detailCache.delete(detailKey(schoolId, studentId));
}

/**
 * One school's detail (GET /admissions/schools/{school_id}) →
 * `SchoolDetailResponse`: { school, requirements, comparison, process_intro,
 * fit, is_on_list, next_steps }. Consent-gated (a 403 is the consent CTA), flag-
 * gated and non-leak (a 404 is "unknown school" OR "feature dark" — the panel
 * treats both as nothing to show), and rate-limited on the search bucket (a
 * 429 is a calm retry, never red). `studentId` threads a guardian acting on a
 * linked minor, so the comparison is against the MINOR's profile.
 *
 * MEMOIZED PER (student, school): every caller shares one request — a hover
 * prefetch, the click that follows it, and a remount all resolve the same
 * promise — bounded at 50 schools / 5 minutes. A FAILED read is evicted at
 * once, so a retry (or a later click after a failed prefetch) really refetches
 * instead of replaying the error.
 */
export function getSchoolDetail(schoolId, studentId = null) {
  const scope = detailCacheScope();
  if (scope !== _detailCacheScope) {
    _detailCache.clear();
    _detailCacheScope = scope;
  }

  const key = detailKey(schoolId, studentId);
  const hit = _detailCache.get(key);
  _detailCache.delete(key);
  if (hit && Date.now() - hit.at < DETAIL_CACHE_TTL_MS) {
    _detailCache.set(key, hit);
    return hit.promise;
  }

  const entry = {
    at: Date.now(),
    // An async IIFE so a SYNCHRONOUS failure (a throwing interceptor, a bad
    // transport) is a rejection the caller can classify, not an exception out
    // of an event handler.
    promise: (async () => {
      const res = await axios.get(
        withStudent(`/schools/${encodeURIComponent(schoolId)}`, studentId),
      );
      return res.data;
    })(),
  };
  _detailCache.set(key, entry);
  while (_detailCache.size > DETAIL_CACHE_MAX) {
    _detailCache.delete(_detailCache.keys().next().value);
  }
  // This handler is also what marks the promise "handled", so a prefetch whose
  // result nobody awaits can never surface as an unhandled rejection.
  entry.promise.catch(() => {
    if (_detailCache.get(key) === entry) _detailCache.delete(key);
  });
  return entry.promise;
}

/**
 * Warm the cache for a school the student is about to open (hover / focus on
 * its toggle). Fire-and-forget: nothing to await, and a failure — a 429 from
 * sweeping the pointer across a list, a 403/404 — is deliberately SILENT here;
 * the real click surfaces it with a proper retry state.
 */
export function prefetchSchoolDetail(schoolId, studentId = null) {
  getSchoolDetail(schoolId, studentId).catch(() => {});
}

// ── Per-school milestones (ADM PR1, D9/D10) ─────────────────────────────────
//
// The dated, family-entered timeline on ONE shortlisted school. Every route is
// scoped to (resolved student, school) server-side, so a school the student
// hasn't pinned — or a milestone id under another target — is a uniform 404.
// The student OR their verified guardian (via `studentId`) may edit (D9).

/**
 * One shortlisted school's timeline (GET .../targets/{school_id}/milestones).
 * Returns { school, count, milestones[], next_milestone, effective_deadline,
 * next_steps } — an ENVELOPE, so an empty timeline still carries the copy that
 * tells a family what to add (no blank panel, no dead end).
 */
export async function listMilestones(schoolId, studentId) {
  const res = await axios.get(
    withStudent(`/targets/${schoolId}/milestones`, studentId),
  );
  return res.data;
}

/**
 * Add one milestone (POST .../targets/{school_id}/milestones) → 201 + the row.
 *
 * `body` = { kind, date: 'YYYY-MM-DD', title?, notes? }. THE TITLE RULE (A6):
 * `title` is REQUIRED for kind='custom' and REJECTED (422) for every other kind
 * — the kind is its own label. The form mirrors both halves client-side so the
 * student gets an instant, warm correction instead of a round-trip.
 */
export async function createMilestone(schoolId, body, studentId) {
  const res = await axios.post(
    withStudent(`/targets/${schoolId}/milestones`, studentId),
    body,
  );
  return res.data;
}

/**
 * "Add to my timeline" (a researched calendar item, `body.calendar_key` set) →
 * { row, created }. The server keeps ONE milestone per item per cycle per
 * school (migration 092): a repeat of the same add — a double surface, a retry,
 * a second tab — answers 200 with the row ALREADY saved instead of 201 with a
 * new one. `created` says which, so a host that counts saved dates counts a date
 * once. A response without a status (a test double) reads as created.
 */
export async function addCalendarMilestone(schoolId, body, studentId) {
  const res = await axios.post(
    withStudent(`/targets/${schoolId}/milestones`, studentId),
    body,
  );
  return { row: res.data, created: res.status !== 200 };
}

/**
 * Edit one milestone (PATCH .../milestones/{id}) — re-date, re-label, or tick
 * it done. `patch` is a partial mask: an omitted key is unchanged, an explicit
 * `null` clears that column. `date` may never be cleared (422). The backend
 * validates the MERGED state, so flipping `kind` to 'custom' without a title is
 * a 422 and flipping away from custom clears the title atomically.
 */
export async function updateMilestone(schoolId, milestoneId, patch, studentId) {
  const res = await axios.patch(
    withStudent(`/targets/${schoolId}/milestones/${milestoneId}`, studentId),
    patch,
  );
  return res.data;
}

/** Remove one milestone (DELETE .../milestones/{id}) → 204 No Content. */
export async function deleteMilestone(schoolId, milestoneId, studentId) {
  await axios.delete(
    withStudent(`/targets/${schoolId}/milestones/${milestoneId}`, studentId),
  );
}

/**
 * Every shortlisted school's researched calendar, merged and grouped by month
 * (GET /admissions/timeline; ADM research PR D/E) → { count, months: [{ month:
 * 'YYYY-MM', items: [...] }], next_steps }. Unlike the milestones above, which
 * are scoped to ONE (student, school), this is the whole list at once: each
 * item is a calendar item plus the school it belongs to and THAT school's own
 * cycle (a list that mixes a boarding school and a college mixes cycles).
 *
 * An empty list still answers 200 with `next_steps`, so the view never
 * dead-ends. Consent-gated (403 is the consent CTA), flag-gated (a 404 is "the
 * feature is dark") and rate-limited (a 429 is a calm retry, never red).
 * `studentId` threads a guardian acting on a linked minor, whose list it is.
 */
export async function getTimeline(studentId) {
  const res = await axios.get(withStudent('/timeline', studentId));
  return res.data;
}

/**
 * PATCH the application-progress lane on a pinned school (GE-PR5).
 *
 * `patch` is a partial mask of {application_status, application_deadline,
 * decision} — send only the fields that changed. A field sent as `null`
 * CLEARS that column; an omitted field is left as-is (the backend reads
 * exclude_unset). The student OR their verified guardian (via studentId) may
 * edit — the backend's `admissions_profile` consent gate authorizes both.
 *
 * Returns the updated StudentTargetSchoolResponse (with the three fields).
 * The backend enforces: `decision` only when the effective status is
 * 'decided' (422), the Literal value domains (422), and the ISO date format
 * (422); the caller surfaces those via friendlyError.
 */
export async function updateTargetApplication(schoolId, patch, studentId) {
  const res = await axios.patch(
    withStudent(`/targets/${schoolId}`, studentId),
    patch,
  );
  return res.data;
}

// ── Experience fit — the "Find your fit" survey + graded shortlist (SF-PR3) ──
//
// The SECOND, PREFERENCE-based fit axis (routes/school_fit.py). Kept on the
// SAME admissions consent gate + the SAME `?student_id=` threading as the
// odds-based surfaces, so `classifyAdmissionsError` routes a 403/404 IDENTICALLY
// here (403 → consent CTA; bare-"Not Found" 404 → the school-fit feature flag is
// off → a NEUTRAL hidden/"not available" state, NEVER an error).
//
// PATHS: BASE already ends in `/admissions`, and the backend routes are
// `/admissions/fit/...` under the `/api/v1` prefix → the full path is
// `/api/v1/admissions/fit/...`. So the path arg here is `/fit/...` (NOT
// `/admissions/fit/...`, which would DOUBLE the `/admissions` segment and 404 —
// the same footgun `getAdmissionsAccess` documents, which the gate would then
// mis-read as "feature off"). `withStudent` prepends BASE exactly once.

/**
 * The caller's experience-fit preference row (GET /admissions/fit/preferences).
 * NEVER a 404 dead end: a caller who hasn't taken the survey gets a 200 empty
 * shape `{ is_set: false }`; a set row is `{ is_set: true, level, targets,
 * weights, desired_tags, updated_at }` with maps defaulted to `{}` (never null).
 * Consent-gated; flag-gated (a bare-"Not Found" 404 = the feature is off).
 */
export async function getFitPreferences(studentId) {
  const res = await axios.get(withStudent('/fit/preferences', studentId));
  return res.data;
}

/**
 * Upsert the caller's experience-fit preferences (PUT /admissions/fit/preferences).
 * The survey submits the FULL preference each time (a re-take REPLACES the row).
 * `body` = { level: 'secondary'|'college', targets?: {dim:1-5}, weights?:
 * {dim:0-5}, desired_tags?: {dim:[tag]} }. The backend validates the two-value
 * level, the 9-dimension key domain, and the numeric ranges → 422 on violation
 * (surfaced via friendlyError). Returns the persisted StudentFitPreferenceResponse.
 */
export async function saveFitPreferences(body, studentId) {
  const res = await axios.put(withStudent('/fit/preferences', studentId), body);
  return res.data;
}

/**
 * The graded pre-visit shortlist (GET /admissions/fit/shortlist). No survey yet
 * → a 200 empty state `{ preferences_set: false, candidates: [], meaning,
 * next_steps }` (no dead end). Otherwise `{ preferences_set: true, level, count,
 * meaning, candidates: [FitCandidateResponse], next_steps }`, level-routed +
 * sorted best-fit-first. Each candidate carries grade A-D, overall, dimensions[],
 * top_reasons[], visit_check, meaning/method (verbatim), and exemplar chips.
 * Consent-gated; flag-gated.
 */
export async function getFitShortlist(studentId) {
  const res = await axios.get(withStudent('/fit/shortlist', studentId));
  return res.data;
}

/**
 * The catalog schools behind one archetype card
 * (GET /admissions/fit/archetypes/{slug}/schools). Returns { archetype: {slug,
 * name, level}, count, grades_estimated, preferences_set, meaning, schools:
 * [{ school_id, name, city, state, control, annual_cost_usd, selectivity_tier,
 * grade, overall, has_own_profile }] }. `grade`/`overall` are null before the
 * survey. Grades here are ESTIMATES (archetype profile + any per-school
 * overrides) — the UI must label them so. Unknown slug → 404 (surfaced as a
 * warm retry, NOT feature-off: the caller only reaches this after the
 * shortlist loaded, so the feature is known-live). Consent-gated; flag-gated.
 */
export async function getArchetypeSchools(slug, studentId) {
  const res = await axios.get(
    withStudent(`/fit/archetypes/${encodeURIComponent(slug)}/schools`, studentId),
  );
  return res.data;
}

/**
 * Turn a student's free-text "what I'm looking for" into SUGGESTED survey
 * answers (POST /admissions/fit/interpret). SELF-ONLY by design: the text is
 * the student's own voice, so this call never threads ?student_id= (a guardian
 * demoing on their own account egresses their OWN text — a minor's text only
 * ever leaves via the minor's own consent-gated session). Returns
 * { suggested_targets: {dim:1-5}, suggested_weights: {dim:0|1|5},
 *   rationales: {dim: str}, uncaptured: [str] } — SUGGESTIONS the survey
 * prefills for the student to review and edit; nothing is persisted until the
 * student saves the survey (the existing PUT). Transient LLM failure → 503
 * (amber retry). Consent-gated (advisor_chat); flag-gated.
 */
export async function interpretFitDescription(description) {
  const res = await axios.post(`${BASE}/fit/interpret`, { description });
  return res.data;
}

// ── Advisor ─────────────────────────────────────────────────────────────────

export async function askAdvisor(message, conversationId, studentId) {
  const body = { message };
  if (conversationId) body.conversation_id = conversationId;
  const res = await axios.post(withStudent('/advisor/ask', studentId), body);
  return res.data;
}

// ── Guardian linkage + consent ──────────────────────────────────────────────

// App.js's policy may send this again, on purpose. For a POST it re-sends after
// a dropped connection or a 408, 429, 502, 503 or 504 answer (a plain 500 it
// re-sends only for GET, HEAD, OPTIONS, PUT and DELETE). A claim for a pending
// link re-issues its code, and the answer that comes back carries the newest
// one, so a re-send leaves nothing dead that is on screen. It also sends no
// email. When no answer comes back at all, the code on screen for that
// student may have been replaced (isOutcomeUnknown, below). Compare verifyLink
// and inviteGuardian, which a re-send would hurt.
export async function claimMinor(usernameOrEmail, relationshipType) {
  const body = { student_username_or_email: usernameOrEmail };
  if (relationshipType) body.relationship_type = relationshipType;
  const res = await axios.post(`${BASE}/guardian/claim`, body);
  return res.data;
}

/**
 * True when the admissions switch answered: ADMISSIONS_ADVISOR_ENABLED is off
 * and the route 404s before doing anything (`_require_feature`, whose bare
 * "Not Found" errors.py copies into the envelope's message). A deploy can flip
 * it while a page is open, so a write can meet it as well as a read. Told
 * apart from a route's own 404 by that message: claim_minor's for an unknown
 * student says "Student not found.".
 */
export function isAdmissionsSwitchedOff(err) {
  return err?.response?.status === 404 && readMessage(err) === 'Not Found';
}

/**
 * True when the server turned the claim down because of the student it
 * names (guardian_linking.py claim_minor): no such student (404, one answer
 * by design for an unknown name, a non-student and the guardian themselves)
 * or a malformed name (422). The claim form shows these at the field, as
 * something to fix. Anything else (no answer, a rate limit, a 5xx, the
 * switch's own 404, either 409) says nothing wrong about the name.
 */
export function isClaimRefused(err) {
  const status = err?.response?.status;
  if (status === 404) return !isAdmissionsSwitchedOff(err);
  return status === 422;
}

/**
 * True for claim_minor's 409 on a link that is already verified ("This
 * student is already linked and verified."): the student confirmed it
 * meanwhile, somewhere else, so the guardian's list is out of date. The
 * claim's other 409 ("Could not create the link; please retry.", a create
 * that lost a race and found nothing to re-issue on) is a failure; only the
 * message tells the two apart.
 */
export function isClaimAlreadyLinked(err) {
  return err?.response?.status === 409 && /already linked/i.test(readMessage(err));
}

/**
 * True when a failed claim may still have happened: no answer at all (the
 * deadline passed, or the connection dropped, possibly on the answer's way
 * back), a status App.js's retry policy sends again (408, 429, 502, 503, 504:
 * an earlier attempt may have done the work and lost its answer, and only the
 * last attempt's status comes back), or any other 5xx (a server can fail
 * after it committed). A claim for the same student REPLACES the code it hands
 * out (claim_minor re-issues on a pending link), so after such a failure the
 * one on screen may be dead. For the writes that are sent once (the invite,
 * the accept), see isSentOnceOutcomeUnknown.
 */
export function isOutcomeUnknown(err) {
  const status = err?.response?.status;
  return !status || status === 408 || status === 429 || status >= 500;
}

// Bounded by HELD_REQUEST_TIMEOUT_MS: the under-13 parent-invite overlay
// (coppa/PlatformConsentScreen) is held open until this answers.
//
// SENT ONCE, AND NO ANSWER IS NOT A "NO". The code is single-use: the server
// spends it as it verifies the link, and from then on answers that same code
// 400, exactly as it answers a wrong one. A re-send after an answer that was
// lost on its way back would therefore come home as that 400 and tell a
// student whose link had just been made that their code is no good, so this
// request opts out of the automatic re-send (SEND_ONCE). A lost answer, like a
// passed deadline, reaches the caller as a failure with no `response`, and
// callers report that as "couldn't check", never as a wrong code
// (isLinkCodeRefused, below).
//
// Still open: the student's OWN second try after such a failure does get the
// 400 if the first one linked. The link is made all the same, and the
// guardian's side shows it (a fresh claim of a verified link is refused 409).
// Closing that takes the server recognising the repeat, or the caller reading
// the student's links back (listLinks) before it reports the 400; neither is
// done yet.
export async function verifyLink(token) {
  const res = await axios.post(
    `${BASE}/guardian/verify`,
    { token },
    { timeout: HELD_REQUEST_TIMEOUT_MS, ...SEND_ONCE },
  );
  return res.data;
}

/**
 * True when the server answered that it does not accept this code:
 * /guardian/verify sends one generic 400 for a wrong, spent or expired code,
 * and a 422 for a malformed one. Anything else says nothing about the code: a
 * failure with no `response` (see verifyLink), a rate limit, a feature that is
 * off. Both screens that take a code decide by this what they may tell the
 * student.
 */
export function isLinkCodeRefused(err) {
  const status = err?.response?.status;
  return status === 400 || status === 422;
}

// Bounded by HELD_REQUEST_TIMEOUT_MS: Guardian access is a spinner until the
// first answer, and its Try again says "Trying again…" until one comes, so a
// stalled connection would hold either for good. A GET is safe to send again,
// so App.js's retries stay on.
export async function listLinks() {
  const res = await axios.get(`${BASE}/guardian/links`, { timeout: HELD_REQUEST_TIMEOUT_MS });
  return res.data;
}

// ── Student-initiated guardian invite + the access summary (POST-AIQ-185) ────

/**
 * The caller's admissions UNLOCK summary (GET /admissions/access). Drives the
 * gate: { unlocked, unlocked_via: 'guardian'|'self'|'teacher'|null, guardians[],
 * pending_invites[{email_masked, status, created_at}], can_request_teacher }.
 * ('self' = an 18+ adult self-serving; can_request_teacher is false for them.)
 * AUTH: the caller's OWN state — no `?student_id=` threading (it reports only
 * the authenticated user's access, never a counterparty's). Flag-gated: a 404
 * means the admissions feature is off (the caller surfaces a neutral state).
 */
export async function getAdmissionsAccess() {
  // BASE already ends in `/admissions`; the route is `/admissions/access` under
  // the `/api/v1` router prefix → the full path is `/api/v1/admissions/access`,
  // so it's `${BASE}/access` here (NOT `${BASE}/admissions/access`, which would
  // double the segment and 404 → the gate would mis-read as "feature off").
  const res = await axios.get(`${BASE}/access`);
  return res.data;
}

/**
 * A STUDENT invites a parent/guardian by EMAIL (POST /admissions/guardian/invite).
 * Creates a pending guardian link + emails the guardian; when email is disabled
 * the response carries `invite_token` + `invite_url` so the student can relay it
 * out-of-band (no dead end). Returns GuardianInviteResponse: { link, email_sent,
 * invite_token?, invite_url?, expires_at? }. The backend validates the email
 * shape (422 on malformed) — we ALSO validate client-side (see `isValidEmail`)
 * for an instant, friendly "check the address" before any request.
 *
 * SENT ONCE. An invite to an address that has one pending REPLACES its link
 * (invite_guardian re-issues the token) and emails again. App.js's policy
 * would send this again after a dropped connection or a 502/503/504, and an
 * invite whose answer was lost on its way back had already gone out: the
 * re-send kills the link in the email just sent (and with email off, the
 * link the lost answer carried was never shown anyway). So it opts out
 * (SEND_ONCE), and a failure with no answer, or a 5xx, reaches the caller as
 * "may have gone out" (isSentOnceOutcomeUnknown), which the form says honestly
 * instead of sending the student straight back to Send.
 *
 * WITH A DEADLINE (INVITE_TIMEOUT_MS), so a stalled connection ends on that
 * honest line rather than "Sending…" for good.
 */
export async function inviteGuardian(guardianEmail, relationshipType) {
  const body = { guardian_email: guardianEmail };
  if (relationshipType) body.relationship_type = relationshipType;
  const res = await axios.post(
    `${BASE}/guardian/invite`,
    body,
    { timeout: INVITE_TIMEOUT_MS, ...SEND_ONCE },
  );
  return res.data;
}

/**
 * The invite's deadline. NOT HELD_REQUEST_TIMEOUT_MS (20 s): the server sends
 * the invite email inline before it answers (guardian_linking.py
 * `_send_guardian_invite_email` → background_tasks `_send_email_smtp`), and
 * smtplib's timeout (backend `smtp_timeout_seconds`, 20 s) bounds each
 * blocking step of that send, not the whole of it. A relay that stalls at
 * connect makes the server give up on the email at about 20 s and answer with
 * the link for the student to pass on; one that stalls a step later answers
 * at about 40 s. A 20 s deadline would race the first of those answers and
 * throw the link away. 45 s lets both arrive; a send slower still ends on the
 * form's "couldn't confirm" line, which is true then too.
 */
export const INVITE_TIMEOUT_MS = 45000;

/**
 * True when a failed write that was SENT ONCE (SEND_ONCE: inviteGuardian,
 * acceptGuardianInvite) may still have happened: no answer at all, or a 5xx
 * (a server, or a proxy in front of it, can fail after the work was done:
 * the invite made and emailed, the token spent). With one attempt the status
 * that came back is that attempt's, so a 408 or a 429 turned it away before
 * any work: nothing happened, and what is on screen is as good as it was.
 * (Compare isOutcomeUnknown, for the claim, which App.js may send again.)
 */
export function isSentOnceOutcomeUnknown(err) {
  const status = err?.response?.status;
  return !status || status >= 500;
}

/**
 * A GUARDIAN accepts a student-initiated invite (POST /admissions/guardian/accept).
 * The counterpart of `inviteGuardian` above: the student's invite email carries a
 * `/guardian/accept#token=…` link; the signed-in guardian submits that raw token
 * and the backend binds the pending email-keyed link to their account (status →
 * 'verified', single-use — the hash is cleared on success). Returns the resulting
 * GuardianLinkResponse. Error contract (guardian_linking.py `accept_guardian_invite`):
 *   400 — invalid / expired / already-used token (ONE generic message, by design:
 *         no oracle on a student's invite state);
 *   403 — the caller is not a guardian account;
 *   404 — the admissions feature is dark (flag off: the bare "Not Found",
 *         isAdmissionsSwitchedOff);
 *   409 — this guardian is already linked to that student.
 *
 * SENT ONCE, WITH A DEADLINE. The token is single-use: the accept spends it,
 * and from then on the server answers it 400, exactly as it answers a wrong
 * one. App.js's policy would send it again after an answer lost on its way
 * back, and the guardian, linked by the first attempt, would be told their
 * invite "can't be used". So it opts out (SEND_ONCE), as verifyLink does; a
 * lost answer, or a 5xx, reaches the page as "may have happened"
 * (isSentOnceOutcomeUnknown). The page says "Accepting the invite…" until an
 * answer comes, so a stalled connection gets HELD_REQUEST_TIMEOUT_MS (the
 * accept sends no email; it is one quick write).
 */
export async function acceptGuardianInvite(token) {
  const res = await axios.post(
    `${BASE}/guardian/accept`,
    { token },
    { timeout: HELD_REQUEST_TIMEOUT_MS, ...SEND_ONCE },
  );
  return res.data;
}

/**
 * Client-side email validation, MIRRORING the backend's GuardianInviteRequest
 * `pattern` (schemas.py: `^[\w\.-]+@[\w\.-]+\.\w+$`) plus its 3..320 length
 * bound, so the field blocks the same shapes the server would 422 — the student
 * gets an instant, warm "check the address" instead of a round-trip. Trims the
 * input first (a trailing space is the most common miss). NOT a full RFC 5322
 * validator on purpose: it is the SAME bound the backend enforces, no looser, no
 * stricter, so the two never disagree about what's acceptable.
 */
export function isValidEmail(value) {
  const email = (value || '').trim();
  if (email.length < 3 || email.length > 320) return false;
  return /^[\w.-]+@[\w.-]+\.\w+$/.test(email);
}

export async function grantConsent(studentId, scope, policyVersion) {
  const res = await axios.post(`${BASE}/consent`, {
    student_id: studentId,
    scope,
    policy_version: policyVersion,
  });
  return res.data;
}

export async function revokeConsent(studentId, scope) {
  // axios DELETE with a body must pass it under `data`.
  const res = await axios.delete(`${BASE}/consent`, {
    data: { student_id: studentId, scope },
  });
  return res.data;
}

// ── Error classification (the shared "no dead ends" branch) ──────────────────

/**
 * Classify an axios error from a gated admissions endpoint into the UX state
 * the component should render. The three components that read the gate
 * (Profile, Shortlist, Advisor) ALL branch on this so a given backend
 * failure routes to the SAME next step everywhere.
 *
 * Returns one of:
 *   'consent'   — 403: the gate is closed (no active consent). Route the
 *                 student to "ask your guardian to set up access" → the
 *                 consent flow. (The backend's 403 detail is deliberately
 *                 generic for non-disclosure; we map the STATUS, not the
 *                 message.)
 *   'no-profile'— 404 with the "create one via PUT" detail: authorized, but
 *                 no profile yet → the profile-intake empty state.
 *   'feature-off'— 404 with the bare "Not Found" (isAdmissionsSwitchedOff):
 *                 the feature is dark-launched/killed → a neutral "not
 *                 available" empty state.
 *   'rate-limit'— 429: backed-off; ask the student to wait a moment.
 *   'error'     — anything else (5xx, network, and a 404 that is neither of
 *                 the two above) → a generic retry message.
 *
 * We distinguish the 404 shapes by the backend's MESSAGE: the no-profile 404
 * carries an actionable "create one via PUT" message, while the feature-off
 * 404 is the bare "Not Found". This is the documented contract in
 * routes/admissions.py (get_admissions_profile docstring). We read it via the
 * shared `readMessage` (envelope `error.message` first, legacy top-level
 * `detail` as a fallback) so the split works on BOTH wire shapes — the pre-unify
 * copy read only `detail` and would have mis-classified an enveloped no-profile
 * 404 as feature-off.
 *
 * ANY OTHER 404 IS A FAILURE, not the switch (PR #709 review, S6). An edge or
 * a proxy answering for a backend it can't reach sends a 404 with a body of
 * its own, and a route's own 404 ("School not found.") is about one thing; the
 * calm "isn't available yet" panel for either would be a dead end a retry may
 * have cleared. Callers that give a route's own 404 a meaning read the status
 * themselves before classifying (SchoolDetailPanel, KeyDatesForList,
 * ExploreSchools).
 */
export function classifyAdmissionsError(err) {
  const status = err?.response?.status;
  const msg = readMessage(err);

  if (status === 403) return 'consent';
  if (status === 404) {
    if (/profile/i.test(msg) && /PUT/i.test(msg)) return 'no-profile';
    if (isAdmissionsSwitchedOff(err)) return 'feature-off';
    return 'error';
  }
  if (status === 429) return 'rate-limit';
  return 'error';
}

// `friendlyError` is imported from ../api/httpError and re-exported at the top
// of this module (the canonical envelope-dual-read reader; H4 unify #68).
